import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  ArrowLeft, BarChart3, RefreshCw, Download, TrendingUp, TrendingDown, DollarSign,
  ChevronDown, ChevronRight, Plus, Minus
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTHS_FULL = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const ROOM_TYPES = {
  'compartilhado': 'Compartilhado',
  'casal': 'Casal',
  'individual': 'Individual',
  'duplo': 'Duplo',
  'triplo': 'Triplo'
};

const CATEGORIES = {
  'all': { label: 'Todas as Categorias', color: 'bg-gray-100' },
  'nacional-aereo': { label: 'Nacional Aéreo', color: 'bg-blue-100' },
  'internacional-aereo': { label: 'Internacional', color: 'bg-purple-100' },
  'rodoviario': { label: 'Rodoviário', color: 'bg-green-100' },
  'passeio-1-dia': { label: 'Passeio 1 Dia', color: 'bg-orange-100' }
};

const AdminDRE = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [manualReservations, setManualReservations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [generalExpenses, setGeneralExpenses] = useState([]);
  const [operationStatuses, setOperationStatuses] = useState({});
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [taxPercentage, setTaxPercentage] = useState(0);
  
  // Estados para expandir/comprimir - Receitas
  const [expandReceitas, setExpandReceitas] = useState(false);
  const [expandInternacional, setExpandInternacional] = useState(false);
  const [expandNacional, setExpandNacional] = useState(false);
  const [expandRodoviario, setExpandRodoviario] = useState(false);
  const [expand1Dia, setExpand1Dia] = useState(false);
  
  // Estados para expandir/comprimir - Despesas
  const [expandDespesas, setExpandDespesas] = useState(false);
  
  // Estados para expandir pacotes individuais dentro de cada categoria
  const [expandedPackages, setExpandedPackages] = useState({});

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resResponse, manualResponse, pkgResponse, opsResponse, generalExpResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reservations/`),
        axios.get(`${BACKEND_URL}/api/manual-reservations/`),
        axios.get(`${BACKEND_URL}/api/packages/?active_only=false`),
        axios.get(`${BACKEND_URL}/api/operational-results/operations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/cash-flow/general-expenses`).catch(() => ({ data: [] }))
      ]);
      
      setReservations(resResponse.data);
      setManualReservations(manualResponse.data);
      setPackages(pkgResponse.data);
      setGeneralExpenses(generalExpResponse.data);
      setOperations(opsResponse.data || []);
      
      // Extrair despesas de todas as operações
      const allExpenses = [];
      const categories = new Set();
      const statuses = {};
      
      (opsResponse.data || []).forEach(op => {
        if (op.expenses && Array.isArray(op.expenses)) {
          op.expenses.forEach(exp => {
            allExpenses.push({
              ...exp,
              package_id: op.package_id,
              date_id: op.date_id,
              operation_date: op.selected_date
            });
            if (exp.category) categories.add(exp.category);
          });
        }
        // Guardar status da operação
        statuses[op.id] = op.status || 'aguardando_data';
      });
      
      setExpenses(allExpenses);
      setExpenseCategories(Array.from(categories).sort());
      
      // Carregar status das operações
      try {
        const statusPromises = (opsResponse.data || []).map(op => 
          axios.get(`${BACKEND_URL}/api/operational-results/operation-status/${op.id}`).catch(() => null)
        );
        const statusResults = await Promise.all(statusPromises);
        const statusMap = {};
        statusResults.forEach((res, idx) => {
          if (res?.data) {
            const op = (opsResponse.data || [])[idx];
            // Usar a chave combinada package_id_date_id para consistência
            const opKey = `${op.package_id}_${op.date_id}`;
            statusMap[opKey] = res.data;
            // Também adicionar com o id original para compatibilidade
            statusMap[op.id] = res.data;
          }
        });
        setOperationStatuses(statusMap);
      } catch (e) {
        console.error('Erro ao carregar status:', e);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  // Função para determinar o mês de competência (1 dia após término da viagem)
  const getCompetenceMonth = (reservation, packageData) => {
    let endDate = null;
    
    // Procurar data de término no pacote
    if (packageData?.dates) {
      const dateInfo = packageData.dates.find(d => d.id === reservation.date_id);
      if (dateInfo?.end_date) {
        endDate = new Date(dateInfo.end_date);
      }
    }
    
    // Se não encontrou, usar selected_date + duration_days
    if (!endDate && reservation.selected_date) {
      const parts = reservation.selected_date.match(/(\d+)/g);
      if (parts && parts.length >= 2) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const year = parts[2] ? parseInt(parts[2]) : new Date().getFullYear();
        endDate = new Date(year < 100 ? 2000 + year : year, month, day);
        
        if (packageData?.duration_days) {
          endDate.setDate(endDate.getDate() + packageData.duration_days);
        }
      }
    }
    
    if (!endDate) {
      endDate = new Date(reservation.created_at);
    }
    
    // Adicionar 1 dia para competência
    const competenceDate = new Date(endDate);
    competenceDate.setDate(competenceDate.getDate() + 1);
    
    return {
      month: competenceDate.getMonth(),
      year: competenceDate.getFullYear()
    };
  };

  // Categorizar pacotes
  const categorizePackage = (category) => {
    if (!category) return 'outros';
    if (category.includes('internacional')) return 'internacional';
    if (category.includes('nacional') && category.includes('aereo')) return 'nacional';
    if (category.includes('rodoviario') || category === 'rodoviario' || category === 'nacional-rodoviario') return 'rodoviario';
    if (category.includes('1-dia') || category === 'passeio-1-dia' || category === 'bate-volta') return '1dia';
    return 'outros';
  };

  // Parse selected_date como "03 a 09 de Setembro/2026" para obter mês/ano
  const parseSelectedDate = (dateStr) => {
    if (!dateStr) return { month: 0, year: new Date().getFullYear() };
    
    const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    const monthMatch = dateStr.match(/(janeiro|fevereiro|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\/(\d{4})/i);
    
    if (monthMatch) {
      return {
        month: monthNames.indexOf(monthMatch[1].toLowerCase()),
        year: parseInt(monthMatch[2])
      };
    }
    
    return { month: 0, year: new Date().getFullYear() };
  };

  // Mapear categoria do filtro para categorização interna
  const getCategoryMatch = (pkgCategory, filterCategory) => {
    if (filterCategory === 'all') return true;
    
    const internalCat = categorizePackage(pkgCategory);
    
    switch (filterCategory) {
      case 'internacional-aereo':
        return internalCat === 'internacional';
      case 'nacional-aereo':
        return internalCat === 'nacional';
      case 'rodoviario':
        return internalCat === 'rodoviario';
      case 'passeio-1-dia':
        return internalCat === '1dia';
      default:
        return true;
    }
  };

  // Verificar se operação deve ser contabilizada baseado no status
  const shouldCountOperation = (packageId, dateId) => {
    const opId = `${packageId}_${dateId}`;
    const status = operationStatuses[opId]?.status || 'aguardando_data';
    // Só contabiliza se for "realizado" ou "aguardando_data"
    return status === 'realizado' || status === 'aguardando_data';
  };

  // Verificar se a operação está com status "realizado"
  const isOperationRealized = (packageId, dateId) => {
    const opId = `${packageId}_${dateId}`;
    const status = operationStatuses[opId]?.status || 'aguardando_data';
    return status === 'realizado';
  };

  // NOVA REGRA: Verificar se deve usar REALIZADO ou ORÇADO
  // - Se status = "realizado" → usar Realizado
  // - Se status = "aguardando_data" + pessoas >= orçado → usar Realizado
  // - Se status = "aguardando_data" + pessoas < orçado → usar Orçado
  const shouldUseRealizedValues = (packageId, dateId) => {
    const opId = `${packageId}_${dateId}`;
    const statusData = operationStatuses[opId];
    const operation = operations.find(op => op.id === opId);
    
    if (!statusData) return true; // Default: usar realizado
    
    const status = statusData.status || 'aguardando_data';
    const budgetPeople = statusData.budget_people || 0;
    
    // Status "realizado" → sempre usar realizado
    if (status === 'realizado') {
      return true;
    }
    
    // Status "aguardando_data" → verificar quantidade de pessoas
    if (status === 'aguardando_data') {
      // Contar pessoas realizadas na operação
      const actualPeople = operation?.total_people || 
                          operation?.customers?.reduce((sum, c) => sum + (c.quantity || 1), 0) || 0;
      
      // Se pessoas >= orçado, usar realizado
      if (actualPeople >= budgetPeople) {
        return true;
      }
      
      // Caso contrário, usar orçado
      return false;
    }
    
    return true; // Default
  };

  // Obter número de pessoas (realizado ou orçado)
  const getOperationPeople = (packageId, dateId) => {
    const opId = `${packageId}_${dateId}`;
    const statusData = operationStatuses[opId];
    const operation = operations.find(op => op.id === opId);
    
    const budgetPeople = statusData?.budget_people || 0;
    const actualPeople = operation?.total_people || 
                        operation?.customers?.reduce((sum, c) => sum + (c.quantity || 1), 0) || 0;
    
    if (shouldUseRealizedValues(packageId, dateId)) {
      return actualPeople;
    }
    
    return budgetPeople;
  };

  // Obter receita orçada para uma operação (budget_people * avg_package_price)
  const getBudgetedRevenue = (packageId, dateId) => {
    const opId = `${packageId}_${dateId}`;
    const status = operationStatuses[opId];
    const operation = operations.find(op => op.id === opId);
    
    if (!status || !operation) return 0;
    
    const budgetPeople = status.budget_people || 0;
    const avgPrice = operation.avg_package_price || 0;
    
    return budgetPeople * avgPrice;
  };

  // Verificar se a operação é futura (ainda não realizada)
  const isOperationFuture = (packageId, dateId) => {
    const opId = `${packageId}_${dateId}`;
    const status = operationStatuses[opId]?.status || 'aguardando_data';
    return status === 'aguardando_data';
  };

  // Processar dados do DRE - Agora com nomes de pacotes
  const dreData = useMemo(() => {
    // Inicializar estrutura de dados
    const data = {
      receitas: {
        internacional: { 
          total: Array(12).fill(0),
          packages: {} // { packageName: { total: Array(12), dates: { dateLabel: Array(12) } } }
        },
        nacional: { 
          total: Array(12).fill(0),
          packages: {}
        },
        rodoviario: { 
          total: Array(12).fill(0),
          packages: {}
        },
        '1dia': { 
          total: Array(12).fill(0),
          packages: {}
        }
      },
      despesas: {},
      impostos: Array(12).fill(0)
    };

    // Inicializar categorias de despesas
    expenseCategories.forEach(cat => {
      data.despesas[cat] = Array(12).fill(0);
    });

    // Helper para adicionar receita por pacote
    const addRevenueByPackage = (category, packageName, dateLabel, amount, monthIdx) => {
      if (!data.receitas[category]) return;
      
      // Usar nome do pacote com data como identificador único
      const packageKey = packageName || 'Sem Nome';
      const dateKey = dateLabel || 'Sem Data';
      
      if (!data.receitas[category].packages[packageKey]) {
        data.receitas[category].packages[packageKey] = {
          total: Array(12).fill(0),
          dates: {}
        };
      }
      
      if (!data.receitas[category].packages[packageKey].dates[dateKey]) {
        data.receitas[category].packages[packageKey].dates[dateKey] = Array(12).fill(0);
      }
      
      data.receitas[category].packages[packageKey].dates[dateKey][monthIdx] += amount;
      data.receitas[category].packages[packageKey].total[monthIdx] += amount;
      data.receitas[category].total[monthIdx] += amount;
    };

    // Processar reservas normais
    reservations.forEach(res => {
      const pkg = packages.find(p => p.id === res.package_id);
      
      // Verificar se a operação deve ser contabilizada
      if (!shouldCountOperation(res.package_id, res.date_id)) return;
      
      // Verificar filtro de categoria
      const pkgCategory = res.package_category || pkg?.category;
      if (!getCategoryMatch(pkgCategory, selectedCategory)) return;
      
      const competence = getCompetenceMonth(res, pkg);
      
      if (competence.year !== selectedYear) return;
      
      // Verificar todos os possíveis status de pagamento confirmado
      const isPaid = res.status === 'confirmed' || res.status === 'completed' || 
                     res.status === 'Confirmada' || res.status === 'Concluída' ||
                     res.payment_status === 'paid' || res.payment_status === 'approved' ||
                     res.payment_status === 'RECEIVED' || res.payment_status === 'CONFIRMED' ||
                     res.payment_status === 'RECEIVED_IN_CASH';
      if (!isPaid) return;

      const category = categorizePackage(pkgCategory);
      // Usar final_total (após descontos) se disponível
      const amount = res.final_total || res.total_amount || 0;
      const monthIdx = competence.month;
      
      // Obter nome do pacote e data
      const packageName = res.package_name || pkg?.name || 'Pacote Desconhecido';
      const dateLabel = res.selected_date || 'Sem Data';

      addRevenueByPackage(category, packageName, dateLabel, amount, monthIdx);
    });

    // Processar reservas manuais
    manualReservations.forEach(res => {
      const pkg = packages.find(p => p.id === res.package_id);
      
      // Verificar se a operação deve ser contabilizada
      if (!shouldCountOperation(res.package_id, res.date_id)) return;
      
      // Verificar filtro de categoria
      const pkgCategory = res.package_category || pkg?.category;
      if (!getCategoryMatch(pkgCategory, selectedCategory)) return;
      
      const competence = getCompetenceMonth(res, pkg);
      
      if (competence.year !== selectedYear) return;
      
      const isPaid = res.status === 'pago_total' || res.status === 'pago_parcial' || res.status === 'atrasado';
      if (!isPaid) return;

      // Para reservas manuais, o total_amount JÁ É o valor final (com desconto já aplicado)
      // O campo discount_amount é apenas informativo, NÃO deve ser subtraído novamente!
      // Use final_total para reservas do site, ou total_amount para reservas manuais
      const amount = res.final_total || res.total_amount || 0;

      const category = categorizePackage(pkgCategory);
      const monthIdx = competence.month;
      
      // Obter nome do pacote e data
      const packageName = res.package_name || pkg?.name || 'Pacote Desconhecido';
      const dateLabel = res.selected_date || 'Sem Data';

      addRevenueByPackage(category, packageName, dateLabel, amount, monthIdx);
    });

    // Processar despesas operacionais
    expenses.forEach(exp => {
      // Verificar se a operação deve ser contabilizada
      const opId = exp.operation_id || `${exp.package_id}_${exp.date_id}`;
      const parts = opId.split('_');
      const packageId = parts[0];
      const dateId = parts.slice(1).join('_');
      
      if (!shouldCountOperation(packageId, dateId)) return;
      
      // Verificar filtro de categoria
      const pkg = packages.find(p => p.id === packageId);
      if (!getCategoryMatch(pkg?.category, selectedCategory)) return;
      
      // Obter a data da operação para o mês de competência
      const operation = operations.find(o => o.id === opId);
      let monthIdx = 0;
      
      // Usar a data da operação (viagem) como competência, não a data da despesa
      if (operation?.selected_date) {
        const competence = parseSelectedDate(operation.selected_date);
        if (competence.year !== selectedYear) return;
        monthIdx = competence.month;
      } else if (exp.date || exp.created_at) {
        const expDate = new Date(exp.date || exp.created_at);
        if (expDate.getFullYear() !== selectedYear) return;
        monthIdx = expDate.getMonth();
      } else {
        return;
      }
      
      const category = exp.category || 'Outros';
      
      if (!data.despesas[category]) {
        data.despesas[category] = Array(12).fill(0);
      }
      
      // NOVA REGRA: Usar a função shouldUseRealizedValues
      const useRealized = shouldUseRealizedValues(packageId, dateId);
      
      let expenseValue = 0;
      
      if (useRealized) {
        // Usar valor realizado (actual_value) se disponível, senão value
        expenseValue = (exp.actual_value !== undefined && exp.actual_value !== null) 
          ? exp.actual_value 
          : (exp.value || 0);
      } else {
        // Usar valor orçado
        // Para custos variáveis, calcular com base no budget_people
        if (exp.cost_type === 'variavel' && exp.cost_per_person > 0) {
          const statusData = operationStatuses[opId];
          const budgetPeople = statusData?.budget_people || exp.quantity || 0;
          expenseValue = exp.cost_per_person * budgetPeople;
        } else {
          expenseValue = exp.value || 0;
        }
      }
      
      data.despesas[category][monthIdx] += expenseValue;
    });

    // Processar despesas gerais (não operacionais)
    generalExpenses.forEach(exp => {
      if (!exp.date) return;
      
      // Parse date como YYYY-MM-DD
      const dateParts = exp.date.split('-');
      if (dateParts.length !== 3) return;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // 0-indexed
      
      if (year !== selectedYear) return;
      
      // Mapear categoria para exibição
      const categoryMap = {
        'site': 'Despesas Gerais - Site/Hospedagem',
        'luz': 'Despesas Gerais - Luz/Energia',
        'uniformes': 'Despesas Gerais - Uniformes',
        'marketing': 'Despesas Gerais - Marketing',
        'outros': 'Despesas Gerais - Outros'
      };
      const category = categoryMap[exp.category] || `Despesas Gerais - ${exp.category || 'Outros'}`;
      
      if (!data.despesas[category]) {
        data.despesas[category] = Array(12).fill(0);
      }
      data.despesas[category][month] += exp.value || 0;
    });

    // Processar operações - Aplicar regra de REALIZADO vs ORÇADO
    // - Status "REALIZADO" → usar valores Realizado
    // - Status "AGUARDANDO DATA" + pessoas >= orçado → usar Realizado
    // - Status "AGUARDANDO DATA" + pessoas < orçado → usar Orçado
    operations.forEach(op => {
      if (!shouldCountOperation(op.package_id, op.date_id)) return;
      
      // Verificar filtro de categoria
      if (!getCategoryMatch(op.package_category, selectedCategory)) return;
      
      // Calcular mês de competência baseado na data da operação
      const dateStr = op.date || op.selected_date || '';
      let competenceMonth = -1;
      let competenceYear = selectedYear;
      
      const monthMatch = dateStr.match(/(janeiro|fevereiro|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\/(\d{4})/i);
      if (monthMatch) {
        const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
        competenceMonth = monthNames.indexOf(monthMatch[1].toLowerCase());
        competenceYear = parseInt(monthMatch[2]);
      }
      
      if (competenceYear !== selectedYear || competenceMonth < 0) return;
      
      const opId = `${op.package_id}_${op.date_id}`;
      const statusData = operationStatuses[opId] || { status: 'aguardando_data', budget_people: 0 };
      const status = statusData.status || 'aguardando_data';
      const budgetPeople = statusData.budget_people || 0;
      const actualPeople = op.customers?.reduce((sum, c) => sum + (c.quantity || 1), 0) || 0;
      const avgPrice = op.avg_package_price || 0;
      
      // Determinar se deve usar REALIZADO ou ORÇADO
      const useRealized = status === 'realizado' || (status === 'aguardando_data' && actualPeople >= budgetPeople);
      
      // Para receita, as reservas já foram processadas acima
      // Aqui apenas adicionamos a diferença (orçado - realizado) quando for usar orçado
      if (!useRealized && budgetPeople > 0) {
        const budgetRevenue = budgetPeople * avgPrice;
        const actualRevenue = op.total_revenue || 0;
        
        if (budgetRevenue > actualRevenue) {
          const category = categorizePackage(op.package_category);
          const packageName = op.package_name || 'Pacote';
          const dateLabel = `${op.date || op.selected_date} (Orçado)`;
          
          // Adiciona a diferença entre orçado e realizado
          const diffRevenue = budgetRevenue - actualRevenue;
          addRevenueByPackage(category, packageName, dateLabel, diffRevenue, competenceMonth);
        }
      }
    });

    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations, manualReservations, packages, expenses, generalExpenses, expenseCategories, selectedYear, selectedCategory, operationStatuses, operations]);

  // Calcular totais
  const calcularTotalLinha = (arr) => arr.reduce((a, b) => a + b, 0);
  
  const calcularTotalReceitas = (monthIdx) => {
    let total = 0;
    Object.keys(dreData.receitas).forEach(cat => {
      if (cat === '1dia') {
        total += dreData.receitas[cat].total[monthIdx];
      } else {
        total += dreData.receitas[cat].total[monthIdx];
      }
    });
    return total;
  };

  const calcularTotalDespesas = (monthIdx) => {
    let total = 0;
    Object.keys(dreData.despesas).forEach(cat => {
      total += dreData.despesas[cat][monthIdx];
    });
    return total;
  };

  const calcularImpostos = (monthIdx) => {
    const receita = calcularTotalReceitas(monthIdx);
    return receita * (taxPercentage / 100);
  };

  const calcularResultado = (monthIdx) => {
    return calcularTotalReceitas(monthIdx) - calcularTotalDespesas(monthIdx) - calcularImpostos(monthIdx);
  };

  // Totais anuais
  const totalReceitasAnual = Array(12).fill(0).map((_, i) => calcularTotalReceitas(i)).reduce((a, b) => a + b, 0);
  const totalDespesasAnual = Array(12).fill(0).map((_, i) => calcularTotalDespesas(i)).reduce((a, b) => a + b, 0);
  const totalImpostosAnual = Array(12).fill(0).map((_, i) => calcularImpostos(i)).reduce((a, b) => a + b, 0);
  const resultadoAnual = totalReceitasAnual - totalDespesasAnual - totalImpostosAnual;

  const exportToCSV = () => {
    const rows = [];
    
    // Header
    rows.push(['Descrição', ...MONTHS, 'TOTAL']);
    
    // Receitas
    rows.push(['RECEITAS', '', '', '', '', '', '', '', '', '', '', '', '']);
    
    // Internacional
    rows.push(['Internacional', ...dreData.receitas.internacional.total.map(v => v || ''), calcularTotalLinha(dreData.receitas.internacional.total)]);
    Object.entries(dreData.receitas.internacional.packages || {}).forEach(([pkgName, pkgData]) => {
      const numDates = Object.keys(pkgData.dates || {}).length;
      const label = numDates > 1 ? `  ${pkgName} (${numDates} datas)` : `  ${pkgName}`;
      rows.push([label, ...pkgData.total.map(v => v || ''), calcularTotalLinha(pkgData.total)]);
    });
    
    // Nacional
    rows.push(['Nacional', ...dreData.receitas.nacional.total.map(v => v || ''), calcularTotalLinha(dreData.receitas.nacional.total)]);
    Object.entries(dreData.receitas.nacional.packages || {}).forEach(([pkgName, pkgData]) => {
      const numDates = Object.keys(pkgData.dates || {}).length;
      const label = numDates > 1 ? `  ${pkgName} (${numDates} datas)` : `  ${pkgName}`;
      rows.push([label, ...pkgData.total.map(v => v || ''), calcularTotalLinha(pkgData.total)]);
    });
    
    // Rodoviário
    rows.push(['Rodoviário', ...dreData.receitas.rodoviario.total.map(v => v || ''), calcularTotalLinha(dreData.receitas.rodoviario.total)]);
    Object.entries(dreData.receitas.rodoviario.packages || {}).forEach(([pkgName, pkgData]) => {
      const numDates = Object.keys(pkgData.dates || {}).length;
      const label = numDates > 1 ? `  ${pkgName} (${numDates} datas)` : `  ${pkgName}`;
      rows.push([label, ...pkgData.total.map(v => v || ''), calcularTotalLinha(pkgData.total)]);
    });
    
    // 1 Dia
    rows.push(['1 Dia', ...dreData.receitas['1dia'].total.map(v => v || ''), calcularTotalLinha(dreData.receitas['1dia'].total)]);
    Object.entries(dreData.receitas['1dia'].packages || {}).forEach(([pkgName, pkgData]) => {
      const numDates = Object.keys(pkgData.dates || {}).length;
      const label = numDates > 1 ? `  ${pkgName} (${numDates} datas)` : `  ${pkgName}`;
      rows.push([label, ...pkgData.total.map(v => v || ''), calcularTotalLinha(pkgData.total)]);
    });
    
    // Total Receitas
    rows.push(['TOTAL RECEITAS', ...Array(12).fill(0).map((_, i) => calcularTotalReceitas(i)), totalReceitasAnual]);
    
    // Despesas
    rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['DESPESAS', '', '', '', '', '', '', '', '', '', '', '', '']);
    Object.keys(dreData.despesas).forEach(cat => {
      rows.push([cat, ...dreData.despesas[cat].map(v => v || ''), calcularTotalLinha(dreData.despesas[cat])]);
    });
    rows.push(['TOTAL DESPESAS', ...Array(12).fill(0).map((_, i) => calcularTotalDespesas(i)), totalDespesasAnual]);
    
    // Impostos
    rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push([`IMPOSTOS (${taxPercentage}%)`, ...Array(12).fill(0).map((_, i) => calcularImpostos(i)), totalImpostosAnual]);
    
    // Resultado
    rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['RESULTADO', ...Array(12).fill(0).map((_, i) => calcularResultado(i)), resultadoAnual]);
    
    const csvContent = rows.map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `DRE_${selectedYear}_${selectedCategory}.csv`;
    link.click();
  };

  const years = [2024, 2025, 2026, 2027];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  // Componente de linha do DRE
  const DRERow = ({ label, data, isHeader, isTotal, isSubItem, isResult, indent = 0 }) => {
    const total = Array.isArray(data) ? calcularTotalLinha(data) : 0;
    const bgClass = isHeader ? 'bg-[#003366] text-white font-bold' : 
                    isTotal ? 'bg-gray-200 font-bold' :
                    isResult ? (resultadoAnual >= 0 ? 'bg-green-100 font-bold text-green-800' : 'bg-red-100 font-bold text-red-800') :
                    isSubItem ? 'bg-gray-50' : 'bg-white';
    
    return (
      <tr className={`${bgClass} border-b`}>
        <td className={`p-2 text-sm sticky left-0 ${bgClass} z-10 min-w-[180px]`} style={{ paddingLeft: `${8 + indent * 16}px` }}>
          {label}
        </td>
        {Array.isArray(data) ? (
          <>
            {data.map((val, idx) => (
              <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                {val === 0 ? '-' : formatPrice(val)}
              </td>
            ))}
            <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-gray-50">
              {total === 0 ? '-' : formatPrice(total)}
            </td>
          </>
        ) : (
          <>
            {Array(13).fill(null).map((_, idx) => (
              <td key={idx} className="p-2 text-right text-sm min-w-[90px]"></td>
            ))}
          </>
        )}
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-full mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <BarChart3 className="w-6 h-6" />
            <h1 className="text-xl font-bold">DRE - Demonstrativo de Resultados</h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Filtro de Categoria */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px] bg-white text-gray-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIES).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Filtro de Ano */}
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] bg-white text-gray-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2 bg-white/10 rounded px-3 py-1">
              <span className="text-sm">Impostos:</span>
              <Input 
                type="number" 
                value={taxPercentage} 
                onChange={(e) => setTaxPercentage(parseFloat(e.target.value) || 0)}
                className="w-16 h-8 text-center bg-white text-gray-900"
                min="0"
                max="100"
                step="0.1"
              />
              <span className="text-sm">%</span>
            </div>
            <Button onClick={loadData} variant="outline" size="sm" className="text-white border-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={exportToCSV} className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-full mx-auto px-4 py-6">
        {/* Cards de Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">Total Receitas</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{formatPrice(totalReceitasAnual)}</p>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-700 font-medium">Total Despesas</span>
              </div>
              <p className="text-2xl font-bold text-red-700">{formatPrice(totalDespesasAnual)}</p>
            </CardContent>
          </Card>

          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-5 h-5 text-orange-600" />
                <span className="text-sm text-orange-700 font-medium">Impostos ({taxPercentage}%)</span>
              </div>
              <p className="text-2xl font-bold text-orange-700">{formatPrice(totalImpostosAnual)}</p>
            </CardContent>
          </Card>

          <Card className={`${resultadoAnual >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-100 border-red-300'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className={`w-5 h-5 ${resultadoAnual >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
                <span className={`text-sm font-medium ${resultadoAnual >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  {resultadoAnual >= 0 ? 'Lucro' : 'Prejuízo'}
                </span>
              </div>
              <p className={`text-2xl font-bold ${resultadoAnual >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                {formatPrice(Math.abs(resultadoAnual))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Badge de Categoria Selecionada */}
        {selectedCategory !== 'all' && (
          <div className="mb-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${CATEGORIES[selectedCategory]?.color} text-gray-800`}>
              Filtrado por: {CATEGORIES[selectedCategory]?.label}
            </span>
          </div>
        )}

        {/* Tabela DRE */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              DRE Anual - {selectedYear}
              {selectedCategory !== 'all' && ` (${CATEGORIES[selectedCategory]?.label})`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#003366] text-white">
                    <th className="p-2 text-left text-sm font-semibold sticky left-0 bg-[#003366] z-20 min-w-[180px]">
                      Descrição
                    </th>
                    {MONTHS.map((m, idx) => (
                      <th key={idx} className="p-2 text-center text-sm font-semibold min-w-[90px]">{m}</th>
                    ))}
                    <th className="p-2 text-center text-sm font-semibold min-w-[100px] bg-[#002244]">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {/* RECEITAS - Header com botão expandir */}
                  <tr className="bg-[#003366] text-white font-bold cursor-pointer hover:bg-[#002855]" onClick={() => setExpandReceitas(!expandReceitas)}>
                    <td className="p-2 text-sm sticky left-0 bg-[#003366] z-10 min-w-[180px] flex items-center gap-2">
                      {expandReceitas ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      RECEITAS
                    </td>
                    {Array(12).fill(0).map((_, idx) => (
                      <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                        {expandReceitas ? '' : formatPrice(calcularTotalReceitas(idx))}
                      </td>
                    ))}
                    <td className="p-2 text-right text-sm font-bold min-w-[100px] bg-[#002244]">
                      {expandReceitas ? '' : formatPrice(totalReceitasAnual)}
                    </td>
                  </tr>
                  
                  {/* Detalhes das Receitas (quando expandido) */}
                  {expandReceitas && (
                    <>
                      {/* Internacional - Mostrar se categoria é 'all' ou 'internacional-aereo' */}
                      {(selectedCategory === 'all' || selectedCategory === 'internacional-aereo') && (
                        <>
                          <tr 
                            className="bg-purple-100 cursor-pointer hover:bg-purple-200"
                            onClick={(e) => { e.stopPropagation(); setExpandInternacional(!expandInternacional); }}
                          >
                            <td className="p-2 text-sm sticky left-0 bg-purple-100 z-10 min-w-[180px] flex items-center gap-2 font-medium">
                              {expandInternacional ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                              Internacional
                            </td>
                            {dreData.receitas.internacional.total.map((val, idx) => (
                              <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                {val === 0 ? '-' : formatPrice(val)}
                              </td>
                            ))}
                            <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-purple-200">
                              {formatPrice(calcularTotalLinha(dreData.receitas.internacional.total))}
                            </td>
                          </tr>
                          {expandInternacional && Object.entries(dreData.receitas.internacional.packages || {}).map(([pkgName, pkgData]) => (
                            <tr key={pkgName} className="bg-gray-50 border-b">
                              <td className="p-2 text-sm sticky left-0 bg-gray-50 z-10 min-w-[180px] pl-8 text-gray-700">
                                {pkgName}
                                {Object.keys(pkgData.dates || {}).length > 1 && (
                                  <span className="text-xs text-gray-400 ml-2">
                                    ({Object.keys(pkgData.dates || {}).length} datas)
                                  </span>
                                )}
                              </td>
                              {pkgData.total.map((val, idx) => (
                                <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                  {val === 0 ? '-' : formatPrice(val)}
                                </td>
                              ))}
                              <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-gray-100">
                                {formatPrice(calcularTotalLinha(pkgData.total))}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                      
                      {/* Nacional - Mostrar se categoria é 'all' ou 'nacional-aereo' */}
                      {(selectedCategory === 'all' || selectedCategory === 'nacional-aereo') && (
                        <>
                          <tr 
                            className="bg-blue-100 cursor-pointer hover:bg-blue-200"
                            onClick={(e) => { e.stopPropagation(); setExpandNacional(!expandNacional); }}
                          >
                            <td className="p-2 text-sm sticky left-0 bg-blue-100 z-10 min-w-[180px] flex items-center gap-2 font-medium">
                              {expandNacional ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                              Nacional
                            </td>
                            {dreData.receitas.nacional.total.map((val, idx) => (
                              <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                {val === 0 ? '-' : formatPrice(val)}
                              </td>
                            ))}
                            <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-blue-200">
                              {formatPrice(calcularTotalLinha(dreData.receitas.nacional.total))}
                            </td>
                          </tr>
                          {expandNacional && Object.entries(dreData.receitas.nacional.packages || {}).map(([pkgName, pkgData]) => (
                            <tr key={pkgName} className="bg-gray-50 border-b">
                              <td className="p-2 text-sm sticky left-0 bg-gray-50 z-10 min-w-[180px] pl-8 text-gray-700">
                                {pkgName}
                                {Object.keys(pkgData.dates || {}).length > 1 && (
                                  <span className="text-xs text-gray-400 ml-2">
                                    ({Object.keys(pkgData.dates || {}).length} datas)
                                  </span>
                                )}
                              </td>
                              {pkgData.total.map((val, idx) => (
                                <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                  {val === 0 ? '-' : formatPrice(val)}
                                </td>
                              ))}
                              <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-gray-100">
                                {formatPrice(calcularTotalLinha(pkgData.total))}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                      
                      {/* Rodoviário - Mostrar se categoria é 'all' ou 'rodoviario' */}
                      {(selectedCategory === 'all' || selectedCategory === 'rodoviario') && (
                        <>
                          <tr 
                            className="bg-green-100 cursor-pointer hover:bg-green-200"
                            onClick={(e) => { e.stopPropagation(); setExpandRodoviario(!expandRodoviario); }}
                          >
                            <td className="p-2 text-sm sticky left-0 bg-green-100 z-10 min-w-[180px] flex items-center gap-2 font-medium">
                              {expandRodoviario ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                              Rodoviário
                            </td>
                            {dreData.receitas.rodoviario.total.map((val, idx) => (
                              <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                {val === 0 ? '-' : formatPrice(val)}
                              </td>
                            ))}
                            <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-green-200">
                              {formatPrice(calcularTotalLinha(dreData.receitas.rodoviario.total))}
                            </td>
                          </tr>
                          {expandRodoviario && Object.entries(dreData.receitas.rodoviario.packages || {}).map(([pkgName, pkgData]) => (
                            <tr key={pkgName} className="bg-gray-50 border-b">
                              <td className="p-2 text-sm sticky left-0 bg-gray-50 z-10 min-w-[180px] pl-8 text-gray-700">
                                {pkgName}
                                {Object.keys(pkgData.dates || {}).length > 1 && (
                                  <span className="text-xs text-gray-400 ml-2">
                                    ({Object.keys(pkgData.dates || {}).length} datas)
                                  </span>
                                )}
                              </td>
                              {pkgData.total.map((val, idx) => (
                                <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                  {val === 0 ? '-' : formatPrice(val)}
                                </td>
                              ))}
                              <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-gray-100">
                                {formatPrice(calcularTotalLinha(pkgData.total))}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                      
                      {/* 1 Dia - Mostrar se categoria é 'all' ou 'passeio-1-dia' */}
                      {(selectedCategory === 'all' || selectedCategory === 'passeio-1-dia') && (
                        <>
                          <tr 
                            className="bg-orange-100 cursor-pointer hover:bg-orange-200"
                            onClick={(e) => { e.stopPropagation(); setExpand1Dia(!expand1Dia); }}
                          >
                            <td className="p-2 text-sm sticky left-0 bg-orange-100 z-10 min-w-[180px] flex items-center gap-2 font-medium">
                              {expand1Dia ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                              Passeio 1 Dia
                            </td>
                            {dreData.receitas['1dia'].total.map((val, idx) => (
                              <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                {val === 0 ? '-' : formatPrice(val)}
                              </td>
                            ))}
                            <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-orange-200">
                              {formatPrice(calcularTotalLinha(dreData.receitas['1dia'].total))}
                            </td>
                          </tr>
                          {expand1Dia && Object.entries(dreData.receitas['1dia'].packages || {}).map(([pkgName, pkgData]) => (
                            <tr key={pkgName} className="bg-gray-50 border-b">
                              <td className="p-2 text-sm sticky left-0 bg-gray-50 z-10 min-w-[180px] pl-8 text-gray-700">
                                {pkgName}
                                {Object.keys(pkgData.dates || {}).length > 1 && (
                                  <span className="text-xs text-gray-400 ml-2">
                                    ({Object.keys(pkgData.dates || {}).length} datas)
                                  </span>
                                )}
                              </td>
                              {pkgData.total.map((val, idx) => (
                                <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                                  {val === 0 ? '-' : formatPrice(val)}
                                </td>
                              ))}
                              <td className="p-2 text-right text-sm font-semibold min-w-[100px] bg-gray-100">
                                {formatPrice(calcularTotalLinha(pkgData.total))}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Total Receitas */}
                  <tr className="bg-green-100 font-bold border-b-2 border-green-300">
                    <td className="p-2 text-sm sticky left-0 bg-green-100 z-10 min-w-[180px]">TOTAL RECEITAS</td>
                    {Array(12).fill(0).map((_, idx) => (
                      <td key={idx} className="p-2 text-right text-sm text-green-700 min-w-[90px]">
                        {formatPrice(calcularTotalReceitas(idx))}
                      </td>
                    ))}
                    <td className="p-2 text-right text-sm text-green-700 font-bold min-w-[100px] bg-green-200">
                      {formatPrice(totalReceitasAnual)}
                    </td>
                  </tr>

                  {/* Espaço */}
                  <tr><td colSpan={14} className="h-4 bg-gray-100"></td></tr>

                  {/* DESPESAS - Header com botão expandir */}
                  <tr className="bg-[#003366] text-white font-bold cursor-pointer hover:bg-[#002855]" onClick={() => setExpandDespesas(!expandDespesas)}>
                    <td className="p-2 text-sm sticky left-0 bg-[#003366] z-10 min-w-[180px] flex items-center gap-2">
                      {expandDespesas ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      DESPESAS
                    </td>
                    {Array(12).fill(0).map((_, idx) => (
                      <td key={idx} className="p-2 text-right text-sm min-w-[90px]">
                        {expandDespesas ? '' : formatPrice(calcularTotalDespesas(idx))}
                      </td>
                    ))}
                    <td className="p-2 text-right text-sm font-bold min-w-[100px] bg-[#002244]">
                      {expandDespesas ? '' : formatPrice(totalDespesasAnual)}
                    </td>
                  </tr>
                  
                  {/* Detalhes das Despesas (quando expandido) */}
                  {expandDespesas && (
                    Object.keys(dreData.despesas).length > 0 ? (
                      Object.keys(dreData.despesas).sort().map(cat => (
                        <DRERow key={cat} label={cat} data={dreData.despesas[cat]} />
                      ))
                    ) : (
                      <tr className="bg-gray-50">
                        <td colSpan={14} className="p-3 text-center text-gray-500 text-sm">
                          Nenhuma categoria de despesa encontrada
                        </td>
                      </tr>
                    )
                  )}
                  
                  {/* Total Despesas */}
                  <tr className="bg-red-100 font-bold border-b-2 border-red-300">
                    <td className="p-2 text-sm sticky left-0 bg-red-100 z-10 min-w-[180px]">TOTAL DESPESAS</td>
                    {Array(12).fill(0).map((_, idx) => (
                      <td key={idx} className="p-2 text-right text-sm text-red-700 min-w-[90px]">
                        {formatPrice(calcularTotalDespesas(idx))}
                      </td>
                    ))}
                    <td className="p-2 text-right text-sm text-red-700 font-bold min-w-[100px] bg-red-200">
                      {formatPrice(totalDespesasAnual)}
                    </td>
                  </tr>

                  {/* Espaço */}
                  <tr><td colSpan={14} className="h-4 bg-gray-100"></td></tr>

                  {/* IMPOSTOS */}
                  <tr className="bg-orange-100 font-bold">
                    <td className="p-2 text-sm sticky left-0 bg-orange-100 z-10 min-w-[180px]">
                      IMPOSTOS ({taxPercentage}%)
                    </td>
                    {Array(12).fill(0).map((_, idx) => (
                      <td key={idx} className="p-2 text-right text-sm text-orange-700 min-w-[90px]">
                        {formatPrice(calcularImpostos(idx))}
                      </td>
                    ))}
                    <td className="p-2 text-right text-sm text-orange-700 font-bold min-w-[100px] bg-orange-200">
                      {formatPrice(totalImpostosAnual)}
                    </td>
                  </tr>

                  {/* Espaço */}
                  <tr><td colSpan={14} className="h-4 bg-gray-100"></td></tr>

                  {/* RESULTADO */}
                  <tr className={`font-bold text-lg ${resultadoAnual >= 0 ? 'bg-blue-200' : 'bg-red-200'}`}>
                    <td className={`p-3 text-sm sticky left-0 z-10 min-w-[180px] ${resultadoAnual >= 0 ? 'bg-blue-200 text-blue-800' : 'bg-red-200 text-red-800'}`}>
                      RESULTADO
                    </td>
                    {Array(12).fill(0).map((_, idx) => {
                      const resultado = calcularResultado(idx);
                      return (
                        <td key={idx} className={`p-3 text-right text-sm min-w-[90px] ${resultado >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                          {formatPrice(resultado)}
                        </td>
                      );
                    })}
                    <td className={`p-3 text-right text-sm font-bold min-w-[100px] ${resultadoAnual >= 0 ? 'bg-blue-300 text-blue-800' : 'bg-red-300 text-red-800'}`}>
                      {formatPrice(resultadoAnual)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDRE;
